import { onMount } from 'solid-js';
import { css } from '@emotion/css';
import { useStore } from 'Shared/StoreProvider';
import { Show,createSignal } from 'solid-js';
import iro from '@jaames/iro';
import { ColourPickerPopup } from 'Shared/forms/ColourPickerPopup';
import { subdoc } from 'Common/ViewUtils';
import { textStyle, updateField } from 'Shared/forms/Inputs';


export function ColourPicker(P)
{
	let node!: HTMLElement;
	const sw = useStore();
	const [popup,setPopup] = createSignal(false);

	const closePopup = () => {
		setPopup(false);
		/* NOTE for the sake of efficiency only updating the database when the popup is closed */
		updateField(node,sw,P.field,P.processInput,P.initParent,()=>{}); 
	};

	return (<>
		<TextField ref={node} {...P} setPopup={setPopup} />

		<Show when={popup()}>
			<PickerPopup {...P} popup={popup()} setPopup={closePopup} />
		</Show>
	</>);
}

function TextField(P)
{
	const fieldStyle = css({
		display: 'flex'
	});

	const patchStyle = css({
		width: '2em',
		border: 'solid 1px black'
	});

	const valueStyle = css(textStyle(true,false), {
		padding: '0 0.5em',
		width: '8em'
	});


	const sw = useStore();

	const value = () => subdoc(sw.store,[...sw.location,P.field]);

	const updateValue = e => {
		/* iro doesn't support colour names, so convert these */
		const converted = colourNameToHex(e.target.value);
		if (converted)
			sw.setStore(...sw.location,P.field,converted);
		updateField(e.target,sw,P.field,P.processInput,P.initParent,()=>{});
	};

	return (
		<div class={fieldStyle}>
			<div class={patchStyle} style={{'background-color':value()}} onClick={() => P.setPopup(!P.popup)} />

			<input ref={P.ref} type='text' class={valueStyle} value={value() ?? ''} maxlength={40} placeholder='(none)' onChange={updateValue} />
		</div>
	);
}

function PickerPopup(P)
{
	const sw = useStore();
	let pickerNode!: HTMLElement;

	const value = () => subdoc(sw.store,[...sw.location,P.field]);

	onMount(() => {
		const colorPicker = new (iro.ColorPicker as any)(pickerNode, {   //FIXME any
			color: value(),
			layout:[{
				component: iro.ui.Wheel,
			}, {
				component: iro.ui.Slider,
				options: {
					sliderType: 'value' 
				}
			}, {
				component: iro.ui.Slider,
				options: {
					sliderType: 'alpha'
				}
			}
		]});

		colorPicker.on(['color:init', 'color:change'], color => {
			sw.setStore(...sw.location,P.field,color.hex8String);
		});
	});

	return (
		<ColourPickerPopup close={() => P.setPopup(false)} value={value()}>
			<div ref={pickerNode} />
		</ColourPickerPopup>
	);
}

/* 
	The only alternative approach I can think of is to test all the possible colour formats, eg '#111', 'rgb(...)' etc.
	In this case an alternative conversion technique is here:
		https://stackoverflow.com/questions/1573053/javascript-function-to-convert-color-names-to-hex-codes
*/
function colourNameToHex(colour)
{
    const colours = {'aliceblue':'#f0f8ff','antiquewhite':'#faebd7','aqua':'#00ffff','aquamarine':'#7fffd4','azure':'#f0ffff',
		'beige':'#f5f5dc','bisque':'#ffe4c4','black':'#000000','blanchedalmond':'#ffebcd','blue':'#0000ff','blueviolet':'#8a2be2','brown':'#a52a2a','burlywood':'#deb887',
		'cadetblue':'#5f9ea0','chartreuse':'#7fff00','chocolate':'#d2691e','coral':'#ff7f50','cornflowerblue':'#6495ed','cornsilk':'#fff8dc','crimson':'#dc143c','cyan':'#00ffff',
		'darkblue':'#00008b','darkcyan':'#008b8b','darkgoldenrod':'#b8860b','darkgray':'#a9a9a9','darkgreen':'#006400','darkkhaki':'#bdb76b','darkmagenta':'#8b008b','darkolivegreen':'#556b2f',
		'darkorange':'#ff8c00','darkorchid':'#9932cc','darkred':'#8b0000','darksalmon':'#e9967a','darkseagreen':'#8fbc8f','darkslateblue':'#483d8b','darkslategray':'#2f4f4f','darkturquoise':'#00ced1',
		'darkviolet':'#9400d3','deeppink':'#ff1493','deepskyblue':'#00bfff','dimgray':'#696969','dodgerblue':'#1e90ff',
		'firebrick':'#b22222','floralwhite':'#fffaf0','forestgreen':'#228b22','fuchsia':'#ff00ff',
		'gainsboro':'#dcdcdc','ghostwhite':'#f8f8ff','gold':'#ffd700','goldenrod':'#daa520','gray':'#808080','green':'#008000','greenyellow':'#adff2f',
		'honeydew':'#f0fff0','hotpink':'#ff69b4',
		'indianred ':'#cd5c5c','indigo':'#4b0082','ivory':'#fffff0','khaki':'#f0e68c',
		'lavender':'#e6e6fa','lavenderblush':'#fff0f5','lawngreen':'#7cfc00','lemonchiffon':'#fffacd','lightblue':'#add8e6','lightcoral':'#f08080','lightcyan':'#e0ffff','lightgoldenrodyellow':'#fafad2',
		'lightgrey':'#d3d3d3','lightgreen':'#90ee90','lightpink':'#ffb6c1','lightsalmon':'#ffa07a','lightseagreen':'#20b2aa','lightskyblue':'#87cefa','lightslategray':'#778899','lightsteelblue':'#b0c4de',
		'lightyellow':'#ffffe0','lime':'#00ff00','limegreen':'#32cd32','linen':'#faf0e6',
		'magenta':'#ff00ff','maroon':'#800000','mediumaquamarine':'#66cdaa','mediumblue':'#0000cd','mediumorchid':'#ba55d3','mediumpurple':'#9370d8','mediumseagreen':'#3cb371','mediumslateblue':'#7b68ee',
		'mediumspringgreen':'#00fa9a','mediumturquoise':'#48d1cc','mediumvioletred':'#c71585','midnightblue':'#191970','mintcream':'#f5fffa','mistyrose':'#ffe4e1','moccasin':'#ffe4b5',
		'navajowhite':'#ffdead','navy':'#000080',
		'oldlace':'#fdf5e6','olive':'#808000','olivedrab':'#6b8e23','orange':'#ffa500','orangered':'#ff4500','orchid':'#da70d6',
		'palegoldenrod':'#eee8aa','palegreen':'#98fb98','paleturquoise':'#afeeee','palevioletred':'#d87093','papayawhip':'#ffefd5','peachpuff':'#ffdab9','peru':'#cd853f','pink':'#ffc0cb','plum':'#dda0dd','powderblue':'#b0e0e6','purple':'#800080',
		'rebeccapurple':'#663399','red':'#ff0000','rosybrown':'#bc8f8f','royalblue':'#4169e1',
		'saddlebrown':'#8b4513','salmon':'#fa8072','sandybrown':'#f4a460','seagreen':'#2e8b57','seashell':'#fff5ee','sienna':'#a0522d','silver':'#c0c0c0','skyblue':'#87ceeb','slateblue':'#6a5acd','slategray':'#708090','snow':'#fffafa','springgreen':'#00ff7f','steelblue':'#4682b4',
		'tan':'#d2b48c','teal':'#008080','thistle':'#d8bfd8','tomato':'#ff6347','turquoise':'#40e0d0',
		'violet':'#ee82ee',
		'wheat':'#f5deb3','white':'#ffffff','whitesmoke':'#f5f5f5',
		'yellow':'#ffff00','yellowgreen':'#9acd32'
	};

    if (colours[colour.toLowerCase()])
        return colours[colour.toLowerCase()];

    return false;
}
